import { Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { ROUTES } from './constants';
import { CookieBanner } from './components/CookieBanner.tsx';
import { MetaManager } from './components/MetaManager.tsx';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import { version } from '../package.json';

/**
 * Implementing code splitting with React.lazy and Suspense for performance optimization
 *
 * Benefits:
 * - Reduces initial bundle size
 * - Improves initial load time
 * - Components are loaded on-demand when routes are accessed
 * - Better performance in production environment
 */

// Lazy load components
// The `.then(module => ({ default: module.Component }))` pattern is used to convert named exports to default exports
// This allows us to use lazy loading without changing the export style in all our component files
const Home = lazy(() =>
  import('./pages/Home').then((module) => ({ default: module.Home })),
);
const AddEditService = lazy(() =>
  import('./pages/addEditService/AddEditService.tsx').then((module) => ({
    default: module.AddEditService,
  })),
);
const Services = lazy(() =>
  import('./pages/Services').then((module) => ({ default: module.Services })),
);
const UserProfile = lazy(() =>
  import('./pages/userProfile/UserProfile.tsx').then((module) => ({
    default: module.UserProfile,
  })),
);
const AdminDashboard = lazy(() =>
  import('./pages/AdminDashboard').then((module) => ({
    default: module.AdminDashboard,
  })),
);
const ServiceDetails = lazy(() =>
  import('./pages/ServiceDetails').then((module) => ({
    default: module.ServiceDetails,
  })),
);
const FAQ = lazy(() =>
  import('./pages/FAQ').then((module) => ({ default: module.FAQ })),
);
const PrivacyPolicy = lazy(() =>
  import('./pages/PrivacyPolicy').then((module) => ({
    default: module.PrivacyPolicy,
  })),
);
const Contact = lazy(() =>
  import('./pages/Contact').then((module) => ({ default: module.Contact })),
);
const NotFound = lazy(() =>
  import('./pages/NotFound').then((module) => ({ default: module.NotFound })),
);
const TermsAndConditions = lazy(() =>
  import('./pages/TermsAndConditions.tsx').then((module) => ({
    default: module.TermsAndConditions,
  })),
);

// Loading component for Suspense fallback
// This is shown while the lazy-loaded component is being fetched
// For production, consider customizing this with your brand styling or using a skeleton loader
const LoadingSpinner = () => (
  <div className='flex justify-center items-center h-screen'>
    <div className='animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary'></div>
  </div>
);

export default function App() {
  console.info(`App version: ${version}, Env: ${import.meta.env.VITE_ENV}`);
  return (
    <>
      <MetaManager />
      <AnalyticsTracker />
      {/* Suspense boundary wraps all routes to handle loading states during code splitting */}
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path={ROUTES.home} element={<Home />} />
          <Route path={ROUTES.addService} element={<AddEditService />} />
          <Route path={ROUTES.editService} element={<AddEditService />} />
          <Route path={ROUTES.services} element={<Services />} />
          <Route path={ROUTES.profile} element={<UserProfile />} />
          <Route path={ROUTES.admin} element={<AdminDashboard />} />
          <Route path={ROUTES.serviceDetails} element={<ServiceDetails />} />
          <Route path={ROUTES.faq} element={<FAQ />} />
          <Route path={ROUTES.privacy} element={<PrivacyPolicy />} />
          <Route path={ROUTES.contact} element={<Contact />} />
          <Route
            path={ROUTES.termsAndConditions}
            element={<TermsAndConditions />}
          />
          <Route path={ROUTES.notFound} element={<NotFound />} />
        </Routes>
      </Suspense>
      <CookieBanner />
    </>
  );
}
